import dayjs from 'dayjs';
import { TimeUnitType } from 'interfaces/drug';

/**
 * @description Returns a future date as a formatted string
 *
 * @SADtrace SAD-003-6.4
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {number} offset - Number of days to add to the current date (default: 1)
 *
 * @returns {string} Future date formatted as 'YYYY-MM-DD'
 */
export function getFutureDate(offset = 1) {
  const today = dayjs();
  const futureDate = today.add(offset, 'day');
  const formattedDate = futureDate.format('YYYY-MM-DD');
  return formattedDate;
}

/**
 * @description Formats a date in long format
 *
 * @SADtrace SAD-003-6.4
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {Date} date - The date to format
 *
 * @returns {string} Date formatted as 'MMMM D, YYYY h:mm:ss A' (e.g., "January 1, 2023 12:30:45 PM")
 */
export function formatMyDateLong(date: Date) {
  return dayjs(date).format('MMMM D, YYYY h:mm:ss A');
}

/**
 * @description Formats a date in short format
 *
 * @SADtrace SAD-003-6.4
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {Date} date - The date to format
 *
 * @returns {string} Date formatted as 'MMM D, YYYY' (e.g., "Jan 1, 2023")
 */
export function formatMyDateShort(date: Date) {
  return dayjs(date).format('MMM D, YYYY');
}

/**
 * @description Formats a date in remix format
 *
 * @SADtrace SAD-003-6.4
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {Date} date - The date to format
 *
 * @returns {string} Date formatted as 'MM-DD-YYYY HH:mm:ss' (e.g., "01-01-2023 12:30:45")
 */
export function formatMyDateRemix(date: Date) {
  return dayjs(date).format('MM-DD-YYYY HH:mm:ss');
}

/**
 * @description Converts seconds to different time formats based on the specified unit
 *
 * @SADtrace SAD-003-4.11, SAD-003-4.14
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {number} seconds - The number of seconds to convert
 * @param {TimeUnitType} unit - The unit to convert to ('seconds', 'minutes', or 'HH:mm')
 *
 * @returns {number|string} Converted time in the specified format
 *   - For 'seconds': returns the original seconds value
 *   - For 'minutes': returns minutes extracted from seconds
 *   - For 'HH:mm': returns time in 24-hour format (e.g., "13:45")
 */
export const formatSecondsToWhateverFormat = (
  seconds: number,
  unit: TimeUnitType,
) => {
  if (unit === 'seconds') {
    return seconds;
  } else if (unit === 'minutes') {
    return dayjs().startOf('day').second(seconds).minute();
  } else if (unit === 'HH:mm') {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return dayjs().hour(hours).minute(minutes).format('HH:mm');
  }
};
